import { http } from '@/core/realisations/Http.realisation'
import { AxiosResponse } from 'axios'
import {
    ClientsListParamsT,
    CreateClientPayloadT,
    CreateMicroservicePayloadT,
    CreatePermissionPayloadT,
    CreateRolePayloadT,
    GatewayGenerateTokenResponseT,
    GatewayGetClientsResponseT,
    GatewayGetMicroservicesResponseT,
    GatewayGetPermissionsResponseT,
    GatewayGetRolesResponseT,
    GatewayGetRoutesParamsT,
    GatewayGetRoutesResponseT,
    GatewayGetScopeRoutesResponseT,
    GatewayGetScopesParamsT,
    GatewayGetScopesResponseT,
    GatewayRouteT,
    MicroservicesListParamsT,
    RolesListParamsT,
} from '../types/gateway.types'

export class GatewayRepository {
    getRoles(
        page: number,
        payload?: RolesListParamsT
    ): Promise<AxiosResponse<GatewayGetRolesResponseT>> {
        const params = {
            page,
            ...payload,
        }

        return http.get('/gateway/roles', { params })
    }

    getPermissions(
        page: number,
        name?: string,
        serviceId?: string
    ): Promise<AxiosResponse<GatewayGetPermissionsResponseT>> {
        const params = {
            page,
            name,
            serviceId,
        }

        return http.get('/gateway/permissions', { params })
    }

    getMicroservices(
        page: number,
        search?: string,
        payload?: MicroservicesListParamsT
    ): Promise<AxiosResponse<GatewayGetMicroservicesResponseT>> {
        const params = {
            page,
            name: search,
            ...payload,
        }

        return http.get('/gateway/microservice', { params })
    }

    getScopes(
        page: number,
        params?: GatewayGetScopesParamsT
    ): Promise<AxiosResponse<GatewayGetScopesResponseT>> {
        const scope = params?.scope
        const isActive = params?.isActive

        return http.get('/gateway/scopes', { params: { page, scope, isActive } })
    }

    getRoutes(
        page: number,
        params?: GatewayGetRoutesParamsT
    ): Promise<AxiosResponse<GatewayGetRoutesResponseT>> {
        const service = params?.service
        const serviceId = params?.serviceId
        const method = params?.method
        const path = params?.path
        const limit = params?.limit

        return http.get('/gateway/routes', {
            params: { page, serviceId, method, path, service, limit },
        })
    }

    getScopeRoutes(
        scope_id: number,
        page: number
    ): Promise<AxiosResponse<GatewayGetScopeRoutesResponseT>> {
        const params = {
            page,
        }

        return http.get(`/gateway/scopes/${scope_id}/accesses`, { params })
    }

    linkRoute(scope_id: number, route_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/gateway/scopes/${scope_id}/accesses/${route_id}/bind`, null)
    }

    unlinkRoute(scope_id: number, route_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/gateway/scopes/${scope_id}/accesses/${route_id}/unbind`, null)
    }

    createScope(
        scope: string,
        description: string,
        isActive: boolean
    ): Promise<AxiosResponse<void>> {
        return http.post('/gateway/scopes', { scope, description, isActive })
    }

    createRoute(route: GatewayRouteT): Promise<AxiosResponse<void>> {
        return http.post('/gateway/routes', route)
    }

    updateScope(
        scope_id: number,
        scope: string,
        description: string,
        isActive: boolean
    ): Promise<AxiosResponse<void>> {
        return http.put(`/gateway/scopes/${scope_id}`, { scope, description, isActive })
    }

    createMicroservice(payload: CreateMicroservicePayloadT): Promise<AxiosResponse<void>> {
        return http.post('/gateway/microservice', payload)
    }

    updateMicroservice(
        microservice_id: number,
        payload: CreateMicroservicePayloadT
    ): Promise<AxiosResponse<void>> {
        return http.put(`/gateway/microservice/${microservice_id}`, payload)
    }

    importAllRoutes(): Promise<AxiosResponse<void>> {
        return http.get('/gateway/route/import')
    }

    importMicroserviceRoutes(microservice_id: number): Promise<AxiosResponse<void>> {
        return http.get(`/gateway/route/import/${microservice_id}`)
    }

    createPermission(payload: CreatePermissionPayloadT): Promise<AxiosResponse<void>> {
        return http.post('/gateway/permissions', payload)
    }

    updatePermission(
        permission_id: number,
        payload: CreatePermissionPayloadT
    ): Promise<AxiosResponse<void>> {
        return http.put(`/gateway/permissions/${permission_id}`, payload)
    }

    createRole(payload: CreateRolePayloadT): Promise<AxiosResponse<void>> {
        return http.post('/gateway/roles', payload)
    }

    updateRole(role_id: number, payload: CreateRolePayloadT): Promise<AxiosResponse<void>> {
        return http.put(`/gateway/roles/${role_id}`, payload)
    }

    updateRoute(route: GatewayRouteT): Promise<AxiosResponse<void>> {
        return http.put(`/gateway/routes/${route.id}`, route)
    }

    deleteRole(role_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/gateway/roles/${role_id}`)
    }

    deleteRoute(route_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/gateway/routes/${route_id}`)
    }

    getClients(
        page: number,
        payload?: ClientsListParamsT
    ): Promise<AxiosResponse<GatewayGetClientsResponseT>> {
        const params = {
            page,
            ...payload,
        }

        return http.get('/gateway/clients', { params })
    }

    createClient(payload: CreateClientPayloadT): Promise<AxiosResponse<void>> {
        return http.post('/gateway/clients', payload)
    }

    updateClient(client_id: number, payload: CreateClientPayloadT): Promise<AxiosResponse<void>> {
        return http.put(`/gateway/clients/${client_id}`, payload)
    }

    deleteClient(client_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/gateway/clients/${client_id}`)
    }

    deletePermission(id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/gateway/permissions/${id}`)
    }

    generateToken(
        user_id: number,
        client_id: number
    ): Promise<AxiosResponse<GatewayGenerateTokenResponseT>> {
        return http.post('/gateway/clients/personal/token', { user_id, client_id })
    }
}
